import { RefObject, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import queryString from 'query-string';
import {
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
} from '@ui/src';
import {
  Cursor,
  CursorByCategoryId,
  CustomerDocument,
  CustomerDocumentStatus,
  DocumentsState,
} from '../types';
import {
  useBatchDeleteCustomerDocumentsMutation,
  useChangeCustomerDocumentsCategoryMutation,
  useChangeCustomerDocumentsStatusMutation,
  useLazyGetCustomerDocumentFileQuery,
  useLazyGetCustomerDocumentsQuery,
  useLazyGetCustomerOrganizationAllowedDocumentsCategoriesQuery,
  useLazyGetCustomerOrganizationDocumentsStatsQuery,
  useRenameCustomerDocumentMutation,
} from '../../../customers/store/services/customers';
import {
  clearAllCursors,
  clearCursorByCategoryId,
  clearSelectedState,
  deleteIsSuccess,
  onDocumentRaname,
  onDocumentsMoveById,
  onSelectAllGroup,
  onSelectAllGroupById,
  setCursor,
  setCursorByCategoryId,
  setNeedToReloadCategoryId,
} from '../../store/slices/documents/documents';
import { useAppDispatch } from '../../../../../../../store/use-store';
import { AllowedCategory } from '../../../new-application/types';
import { OrganizationType } from '../../types';

let timeout: NodeJS.Timeout;

const useDocuments = ({
  customerId,
  scrollWrapper,
  allDocuments,
  documentsByCategoryId,
  cursorByCategoryId,
  cursor,
  needToReloadCategoryId,
  allowedCategories,
  wrapperRef,
  activePermissions,
  type: customerType,
  selectedDocumentsByGroupId,
}: {
  customerId?: string;
  scrollWrapper: RefObject<HTMLDivElement | null>;
  allDocuments?: DocumentsState;
  documentsByCategoryId?: DocumentsState;
  cursorByCategoryId?: CursorByCategoryId;
  cursor?: Cursor;
  needToReloadCategoryId?: string;
  allowedCategories?: AllowedCategory[];
  wrapperRef: RefObject<HTMLDivElement | null>;
  activePermissions?: PermissionEnum[];
  type: OrganizationType;
  selectedDocumentsByGroupId: Record<string, number>;
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<
    CustomerDocumentStatus | undefined
  >();
  const [otherActionsMenuVisible, setOtherActionsMenuVisible] = useState(false);

  const [activeLabel, setActiveLabel] = useState(false);
  const [withScrollButton, setWithScrollButton] = useState(false);
  const [currentScrollValue, setCurrentValueScroll] = useState(0);
  const [contentScrollWidth, setContentScrollWidth] = useState(0);
  const [withLeftButton, setWithLeftButton] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    category: string;
  }>();
  const [renamePopupVisible, setRenamePopupVisible] = useState(false);
  const [selectedDocumentToRename, setSelectedDocumentToRename] = useState<
    CustomerDocument | undefined
  >();
  const [documentName, setDocumentName] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sort, setSort] = useState('asc');
  const [sortProp, setSortProp] = useState('createdAt');
  const [nameError, setNameError] = useState('');
  const [
    selectedDocumentsCountByCategory,
    setSelectedDocumentsCountByCategory,
  ] = useState(0);
  const [subMenuVisible, setSubMenuVisible] = useState(false);
  const [expiredDocumentsVisible, setExpiredDocumentsVisible] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [selectedSubMenuDocumentId, setSelectedSubMenuDocumentId] =
    useState('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    setError,
    formState: { errors },
  } = useForm();

  const prevScrollValue = usePrevious(currentScrollValue);
  const prevSearchText = usePrevious(searchText);
  const prevStatuses = usePrevious(selectedStatuses);

  const [getCustomerDocuments, { isLoading, isFetching: documentsIsFetching }] =
    useLazyGetCustomerDocumentsQuery();

  const [getAllowedCategories] =
    useLazyGetCustomerOrganizationAllowedDocumentsCategoriesQuery();
  const [
    getCustomerDocumentFile,
    { isLoading: downloadInProgress, isFetching },
  ] = useLazyGetCustomerDocumentFileQuery();
  const [
    deleteCustomerDocuments,
    { isLoading: deleteInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useBatchDeleteCustomerDocumentsMutation();
  const [
    changeCustomerDocumentsStatus,
    { isLoading: statusChangeInProgress, isSuccess: statusChangeIsSuccess },
  ] = useChangeCustomerDocumentsStatusMutation();
  const [changeCustomerDocumentsCategory, { isLoading: moveInProgress }] =
    useChangeCustomerDocumentsCategoryMutation();

  const [getCustomerOrganizationDocumentsStats] =
    useLazyGetCustomerOrganizationDocumentsStatsQuery();
  const [rename, { isLoading: documentRenameInProgress }] =
    useRenameCustomerDocumentMutation();

  const onChangeSort = (value: string, propValue: string) => {
    setSort(value);
    setSortProp(propValue);

    let categoryId = '';

    const query = `?take=20&sort=${value}&sortProp=${sortProp}&documentCategoryId=${selectedCategory?.id}${
      selectedStatuses?.length
        ? `&status=${selectedStatuses?.join('&status=')}`
        : ''
    }${searchText ? `&name=${searchText}` : ''}`;

    if (selectedCategory?.id !== 'all') {
      categoryId = selectedCategory?.id as string;
    }

    dispatch(clearAllCursors());

    const body: {
      id: string;
      take: string;
      sort: string;
      sortProp: string;
      reload?: boolean;
      categoryId?: string;
      status?: string[];
      name?: string;
    } = {
      id: customerId as string,
      take: '20',
      sort: value,
      sortProp: propValue,
      reload: true,
    };

    if (categoryId) {
      body.categoryId = categoryId;
    }

    if (selectedStatuses?.length) {
      body.status = selectedStatuses;
    }

    if (searchText) {
      body.name = searchText;
    }

    getCustomerDocuments(body);

    navigate(query);
  };

  const onRename = async () => {
    if (documentName?.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      const response = (await rename({
        id: customerId as string,
        documentId: selectedDocumentToRename?.id as string,
        body: {
          name: documentName,
        },
      })) as { error?: ResponseError };

      if (response && !response.error) {
        dispatch(
          onDocumentRaname({
            name: documentName,
            type: selectedDocumentToRename?.documentCategoryId as string,
            documentId: selectedDocumentToRename?.id as string,
          }),
        );

        setRenamePopupVisible(false);
        setSelectedDocumentToRename(undefined);
        setDocumentName('');
      } else if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const onScrollButton = (directory: 'left' | 'right') => {
    if (scrollWrapper && scrollWrapper.current) {
      const currentScroll = scrollWrapper.current.scrollLeft;
      const width = scrollWrapper.current.offsetWidth;

      let scrollValue = 0;

      if (directory === 'right') {
        scrollValue = currentScroll + 150;

        if (scrollValue + width >= contentScrollWidth - 100) {
          setWithScrollButton(false);
        }
      } else if (directory === 'left') {
        scrollValue = currentScroll - 150;

        if (contentScrollWidth > currentScroll + width - 150) {
          setWithScrollButton(true);
        }

        if (scrollValue <= 0) {
          setWithLeftButton(false);
        }
      }

      scrollWrapper.current.scroll({
        left: scrollValue,
        behavior: 'smooth',
      });

      setCurrentValueScroll(scrollValue);
    }
  };

  const onGroupByIdSelect = ({
    id,
    selected,
  }: {
    id: string;
    selected: boolean;
  }) => {
    dispatch(onSelectAllGroupById({ id, selected }));
  };

  const onGroupSelect = ({
    type,
    selected,
  }: {
    type: string;
    selected: boolean;
  }) => {
    dispatch(onSelectAllGroup({ type, selected }));
  };

  const changeCustomerDocumentsStatusAction = (
    status: CustomerDocumentStatus,
    selectedIds: string[],
  ) => {
    if (selectedIds.length) {
      changeCustomerDocumentsStatus({
        id: customerId as string,
        body: {
          ids: selectedIds,
          status,
        },
      });
    }
  };

  const onDelete = async (selectedIds: string[]) => {
    if (selectedIds.length) {
      const response = await deleteCustomerDocuments({
        id: customerId as string,
        documentId: selectedIds,
      });

      if (response) {
        setDeletePopupVisible(false);

        dispatch(deleteIsSuccess({ ids: selectedIds }));
        getCustomerOrganizationDocumentsStats(customerId as string);
      }
    }
  };

  const onSelectStatuses = async (statuses: string[]) => {
    setSelectedStatuses(statuses);

    let categoryId = '';

    let query = `?take=20&documentCategoryId=${selectedCategory?.id}&sort=${sort}&sortProp=${sortProp}${
      searchText ? `&name=${searchText}` : ''
    }`;

    if (selectedCategory?.id !== 'all') {
      categoryId = selectedCategory?.id as string;
    }

    if (statuses?.length) {
      query = `${query}&status=${statuses.join('&status=')}`;
    }

    navigate(query);

    const body: {
      id: string;
      take: string;
      sort: string;
      sortProp: string;
      categoryId?: string;
      status?: string[];
      name?: string;
      reload: boolean;
    } = {
      id: customerId as string,
      sort,
      sortProp,
      take: '20',
      reload: true,
    };

    if (categoryId) {
      body.categoryId = categoryId;
    }

    if (searchText) {
      body.name = searchText;
    }

    if (statuses?.length) {
      body.status = statuses;
    }

    dispatch(clearAllCursors());

    const response = (await getCustomerDocuments(body)) as {
      error?: any;
      data: any;
    };

    if (response && !response.error) {
      if (categoryId !== 'all') {
        dispatch(
          setCursorByCategoryId({
            startCursor: response?.data?.data?.pageInfo.startCursor,
            endCursor: response?.data?.data?.pageInfo.endCursor,
            take: '20',
            categoryId,
          }),
        );
      }

      if (!categoryId) {
        dispatch(
          setCursor({
            startCursor: response?.data?.data?.pageInfo.startCursor,
            endCursor: response?.data?.data?.pageInfo.endCursor,
            take: '20',
          }),
        );
      }
    }
  };

  const getFiles = async (selectedDocuments: CustomerDocument[]) => {
    if (selectedDocuments.length) {
      const downloadedFiles = await Promise.all(
        selectedDocuments.map(async (item) => {
          const response = await getCustomerDocumentFile({
            id: customerId as string,
            documentId: item.id,
          });
          return response.data;
        }),
      );
      if (downloadedFiles) {
        downloadedFiles.forEach((file, index) => {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;
          // @ts-ignore
          const blob = url.createObjectURL(file);
          hiddenElement.href = blob;
          hiddenElement.target = '_blank';
          hiddenElement.download = selectedDocuments[index].name;
          hiddenElement.click();
        });
        dispatch(clearSelectedState());
      }
    }
  };

  const onSetPage = async (direction: 'prev' | 'next') => {
    let documentsObject;

    if (selectedCategory?.id === 'all') {
      documentsObject = allDocuments;
    } else if (selectedCategory?.id !== 'all') {
      documentsObject = documentsByCategoryId;
    }

    let cursorValue = '';

    if (selectedCategory) {
      if (selectedCategory?.id !== 'all') {
        if (cursorByCategoryId && cursorByCategoryId[selectedCategory.id]) {
          if (direction === 'next') {
            cursorValue = cursorByCategoryId[selectedCategory.id]
              .endCursor as string;
          } else if (direction === 'prev') {
            cursorValue = cursorByCategoryId[selectedCategory.id]
              .startCursor as string;
          }
        }
      } else if (selectedCategory.id === 'all') {
        if (cursor) {
          if (direction === 'next') {
            cursorValue = cursor.endCursor;
          } else if (direction === 'prev') {
            cursorValue = cursor.startCursor;
          }
        }
      }

      let query = search
        .split('&')
        ?.filter((item) => !item.includes('cursor') && !item.includes('take'))
        ?.join('&');

      let requestAvailable = true;

      if (documentsObject && documentsObject[selectedCategory.id]) {
        let cursorItem;

        if (
          selectedCategory.id !== 'all' &&
          cursorByCategoryId &&
          cursorByCategoryId[selectedCategory.id]
        ) {
          cursorItem = cursorByCategoryId[selectedCategory.id];
        } else if (selectedCategory.id === 'all' && cursor) {
          cursorItem = cursor;
        }

        if (
          cursorItem &&
          direction === 'prev' &&
          documentsObject[selectedCategory.id]?.find(
            (item) => item.prevStartCursor === cursorItem.startCursor,
          )
        ) {
          requestAvailable = false;

          const prevPage = documentsObject[selectedCategory.id]?.find(
            (item) => item.prevStartCursor === cursorItem.startCursor,
          );

          if (prevPage) {
            if (prevPage.hasPrev) {
              query = `${query || ''}${
                query ? '&' : '?'
              }cursor=${cursorValue}&take=-20`;
            }

            if (selectedCategory.id !== 'all') {
              dispatch(
                setCursorByCategoryId({
                  startCursor: prevPage.startCursor,
                  endCursor: prevPage.endCursor,
                  take: '-20',
                  categoryId: selectedCategory.id,
                }),
              );
            } else if (selectedCategory.id === 'all') {
              dispatch(
                setCursor({
                  startCursor: prevPage.startCursor,
                  endCursor: prevPage.endCursor,
                  take: '-20',
                }),
              );
            }
          }
        } else if (
          cursorItem &&
          direction === 'next' &&
          documentsObject[selectedCategory.id]?.find(
            (item) => item.prevEndCursor === cursorItem.endCursor,
          )
        ) {
          requestAvailable = false;

          query = `${query || ''}${
            query ? '&' : '?'
          }cursor=${cursorValue}&take=20`;

          const nextPage = documentsObject[selectedCategory.id]?.find(
            (item) => item.prevEndCursor === cursorItem.endCursor,
          );

          if (nextPage) {
            if (selectedCategory.id !== 'all') {
              dispatch(
                setCursorByCategoryId({
                  startCursor: nextPage.startCursor,
                  endCursor: nextPage.endCursor,
                  take: '20',
                  categoryId: selectedCategory.id,
                }),
              );
            } else if (selectedCategory.id === 'all') {
              dispatch(
                setCursor({
                  startCursor: nextPage.startCursor,
                  endCursor: nextPage.endCursor,
                  take: '20',
                }),
              );
            }
          }
        }
      }

      if (requestAvailable) {
        const body: {
          id: string;
          cursor: string;
          take: string;
          sort: string;
          sortProp: string;
          categoryId?: string;
          name?: string;
          status?: string[];
        } = {
          id: customerId as string,
          cursor: cursorValue,
          take: direction === 'next' ? '20' : '-20',
          sort,
          sortProp,
        };

        if (selectedCategory.id !== 'all') {
          body.categoryId = selectedCategory.id;
        }

        if (searchText) {
          body.name = searchText;
        }

        if (selectedStatuses?.length) {
          body.status = selectedStatuses;
        }

        query = `${query || ''}${query ? '&' : '?'}cursor=${cursorValue}&take=${
          direction === 'next' ? '20' : '-20'
        }${searchText ? `&name=${searchText}` : ''}`;

        const response = await getCustomerDocuments(body);

        if (response?.data?.data) {
          if (selectedCategory.id !== 'all') {
            dispatch(
              setCursorByCategoryId({
                startCursor: response?.data?.data.pageInfo.startCursor,
                endCursor: response?.data?.data.pageInfo.endCursor,
                take: direction === 'next' ? '20' : '-20',
                categoryId: selectedCategory.id,
              }),
            );
          } else if (selectedCategory.id === 'all') {
            dispatch(
              setCursor({
                startCursor: response?.data?.data.pageInfo.startCursor,
                endCursor: response?.data?.data.pageInfo.endCursor,
                take: direction === 'next' ? '20' : '-20',
              }),
            );
          }
        }
      }

      navigate(`${query.includes('?') ? '' : '?'}${query}`);
    }
  };

  const onSearch = (
    text: string,
    withoutTimeout?: boolean,
    category?: string,
  ) => {
    clearTimeout(timeout);

    setSearchText(text);

    let query = `?take=20${
      selectedStatuses?.length
        ? `&status=${selectedStatuses.join('&status=')}`
        : ''
    }&documentCategoryId=${
      category || selectedCategory?.id
    }&sort=${sort}&sortProp=${sortProp}`;

    let categoryId = '';

    if (text) {
      query = `${query}&name=${text}`;
    }

    if ((category && category !== 'all') || selectedCategory?.id !== 'all') {
      categoryId = category || (selectedCategory?.id as string);
    }

    timeout = setTimeout(
      async () => {
        navigate(query);

        const response = (await getCustomerDocuments({
          id: customerId as string,
          categoryId: categoryId === 'all' ? '' : categoryId,
          take: '20',
          name: text,
          reload: true,
          sort,
          sortProp,
        })) as { error?: any; data: any };

        if (response && !response.error) {
          if (categoryId !== 'all') {
            dispatch(
              setCursorByCategoryId({
                startCursor: response?.data?.data?.pageInfo.startCursor,
                endCursor: response?.data?.data?.pageInfo.endCursor,
                take: '20',
                categoryId,
              }),
            );
          }

          if (categoryId === 'all' || !categoryId) {
            dispatch(
              setCursor({
                startCursor: response?.data?.data?.pageInfo.startCursor,
                endCursor: response?.data?.data?.pageInfo.endCursor,
                take: '20',
              }),
            );
          }
        }
      },
      withoutTimeout ? 0 : 700,
    );
  };

  const onScrollToCategory = (categoryId: string) => {
    const currentCategory = document.getElementById(categoryId);

    if (
      scrollWrapper &&
      scrollWrapper.current &&
      currentCategory &&
      scrollWrapper?.current?.offsetWidth < scrollWrapper?.current?.scrollWidth
    ) {
      let left = currentCategory.offsetLeft;

      if (scrollWrapper && scrollWrapper.current) {
        const width = scrollWrapper.current.offsetWidth;

        setCurrentValueScroll(left);

        if (left + width >= contentScrollWidth - 100) {
          setWithScrollButton(false);
        }

        if (
          prevScrollValue &&
          withLeftButton &&
          (left < prevScrollValue || left > prevScrollValue)
        ) {
          left -= 30;
        }

        scrollWrapper.current.scroll({
          left,
          behavior: 'smooth',
        });
      }
    }
  };

  const onCategoryClick = (
    value: { id: string; category: string },
    reload?: boolean,
  ) => {
    setSelectedCategory(value);

    let query = `?documentCategoryId=${value.id}${
      selectedStatuses?.length
        ? `&status=${selectedStatuses?.join('&status=')}`
        : ''
    }&sort=${sort}&sortProp=${sortProp}`;

    let categoryId = '';

    if (value.id !== 'all') {
      categoryId = value.id;
    }

    let requestAvailable = true;

    if (!searchText) {
      if (
        value.id === 'all' &&
        needToReloadCategoryId !== 'all' &&
        allDocuments
      ) {
        if (cursor && cursor?.startCursor && cursor?.endCursor && !reload) {
          requestAvailable = false;

          const currentPage = allDocuments?.all.find(
            (item) => item.endCursor === cursor.endCursor,
          );

          if (currentPage) {
            query = `${query}${
              currentPage.prevEndCursor || currentPage.prevStartCursor
                ? `&cursor=${
                    currentPage.prevEndCursor
                      ? currentPage.prevEndCursor
                      : currentPage.prevStartCursor
                  }`
                : ''
            }&take=${cursor?.take || '20'}`;
          }
        }
      } else if (
        value.id !== 'all' &&
        cursorByCategoryId &&
        cursorByCategoryId[value.id] &&
        cursorByCategoryId[value.id]?.startCursor &&
        cursorByCategoryId[value.id]?.endCursor &&
        !reload &&
        needToReloadCategoryId !== value.id
      ) {
        requestAvailable = false;

        if (documentsByCategoryId && documentsByCategoryId[value.id]) {
          const currentPage = documentsByCategoryId[value.id].find(
            (item) => item.endCursor === cursorByCategoryId[value.id].endCursor,
          );

          if (currentPage) {
            query = `${query}${
              currentPage.prevEndCursor || currentPage.prevStartCursor
                ? `&cursor=${
                    currentPage.prevEndCursor
                      ? currentPage.prevEndCursor
                      : currentPage.prevStartCursor
                  }`
                : ''
            }&take=${cursor?.take || '20'}`;
          }
        }
      }

      navigate(query);

      if (requestAvailable) {
        getCustomerDocuments({
          id: customerId as string,
          categoryId,
          take: '20',
          reload: Boolean(needToReloadCategoryId === value.id) || reload,
          status: selectedStatuses,
          sort,
          sortProp,
        });
      }
    } else {
      onSearch(searchText, true, value.id);
    }

    onScrollToCategory(value.id);
  };

  const getFile = async (documentItem: CustomerDocument) => {
    const response = await getCustomerDocumentFile({
      id: customerId as string,
      documentId: documentItem.id,
    });

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = documentItem.name;
      hiddenElement.click();

      setSelectedSubMenuDocumentId('');
      setSubMenuVisible(false);
    }
  };

  const changeCustomerDocumentsCategoryAction = async (
    documentCategoryId: string,
    selectedIds: string[],
  ) => {
    if (selectedIds.length) {
      const body: {
        ids: string[];
        documentCategoryId?: string | undefined;
      } = {
        ids: selectedIds,
        documentCategoryId,
      };

      const response = (await changeCustomerDocumentsCategory({
        id: customerId as string,
        body,
      })) as { error?: any };

      if (response && !response.error) {
        getCustomerOrganizationDocumentsStats(customerId as string);

        dispatch(
          onDocumentsMoveById({
            documentCategoryId,
            ids: selectedIds,
            type: selectedCategory?.id,
          }),
        );

        if (documentCategoryId === selectedCategory?.id) {
          const currentCategory = allowedCategories?.find(
            (item) => item.id === documentCategoryId,
          );

          dispatch(clearCursorByCategoryId(documentCategoryId));

          if (currentCategory) {
            onCategoryClick(
              currentCategory as { id: string; category: string },
              true,
            );
          }
        } else {
          dispatch(setNeedToReloadCategoryId(selectedCategory?.id as string));
        }
      }
    }
  };

  useEffect(() => {
    if (statusChangeIsSuccess) {
      dispatch(clearSelectedState());
      setSelectedStatus(undefined);
      setOtherActionsMenuVisible(false);
    }
  }, [statusChangeIsSuccess]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (scrollWrapper && scrollWrapper.current) {
        if (
          scrollWrapper?.current?.scrollWidth >
          scrollWrapper?.current?.offsetWidth
        ) {
          setWithScrollButton(true);
        } else {
          setWithScrollButton(false);
        }

        setContentScrollWidth(scrollWrapper?.current?.scrollWidth);
      }
    });

    if (wrapperRef && wrapperRef.current && allowedCategories?.length) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [wrapperRef, scrollWrapper, allowedCategories]);

  useEffect(() => {
    if (currentScrollValue > 0) {
      setWithLeftButton(true);
    }
  }, [currentScrollValue]);

  useEffect(() => {
    if (search && allowedCategories) {
      const parsed = queryString.parse(search);

      if (parsed) {
        if (parsed.documentCategoryId && !selectedCategory) {
          if (parsed.documentCategoryId === 'all') {
            setSelectedCategory({ id: 'all', category: 'all' });
          } else if (parsed.documentCategoryId !== 'all') {
            const currentCategory = allowedCategories?.find(
              (item) => item.id === parsed.documentCategoryId,
            );

            onScrollToCategory(parsed.documentCategoryId as string);

            if (currentCategory) {
              setSelectedCategory({
                id: currentCategory.id,
                category: currentCategory?.category || '',
              });
            }
          }
        }
      }
    } else if (!search && !selectedCategory) {
      setSelectedCategory({ id: 'all', category: 'all' });
      navigate(`?documentCategoryId=all&sort=${sort}&sortProp=${sortProp}`);
    }
  }, [search, allowedCategories]);

  useEffect(() => {
    if (selectedCategory && search) {
      const parsed = queryString.parse(search);

      if (
        selectedCategory.id === 'all' &&
        allDocuments &&
        !cursor &&
        allDocuments.all
      ) {
        const currentValues = allDocuments.all[0];

        if (currentValues) {
          dispatch(
            setCursor({
              startCursor: currentValues.startCursor,
              endCursor: currentValues.endCursor,
              take: (parsed?.take as string) || '20',
            }),
          );
        }
      } else if (
        selectedCategory.id !== 'all' &&
        documentsByCategoryId &&
        documentsByCategoryId[selectedCategory.id] &&
        !cursorByCategoryId
      ) {
        const currentValues = documentsByCategoryId[selectedCategory.id][0];

        if (currentValues) {
          dispatch(
            setCursorByCategoryId({
              startCursor: currentValues.startCursor,
              endCursor: currentValues.endCursor,
              take: (parsed?.take as string) || '20',
              categoryId: selectedCategory.id,
            }),
          );
        }
      }
    }
  }, [
    cursor,
    cursorByCategoryId,
    search,
    allDocuments,
    documentsByCategoryId,
    selectedCategory,
  ]);

  useEffect(() => {
    if (customerId && !selectedCategory) {
      if (
        (customerType === OrganizationType.INDIVIDUAL &&
          activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
          )) ||
        (customerType === OrganizationType.CORPORATE &&
          activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
          ))
      ) {
        const body: {
          id: string;
          take: string;
          sort: string;
          categoryId?: string;
          cursor?: string;
          name?: string;
          status?: string[];
          sortProp: string;
        } = {
          id: customerId,
          take: '20',
          sort,
          sortProp,
        };

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.documentCategoryId !== 'all') {
            body.categoryId = parsed.documentCategoryId as string;
          }

          if (parsed.cursor) {
            body.cursor = parsed.cursor as string;
          }

          if (parsed.take) {
            body.take = parsed.take as string;
          }

          if (parsed.name) {
            body.name = parsed.name as string;

            setSearchText(parsed.name as string);
          }

          if (parsed.status) {
            setSelectedStatuses(
              Array.isArray(parsed.status)
                ? (parsed.status as string[])
                : [parsed.status],
            );

            body.status = Array.isArray(parsed.status)
              ? (parsed.status as string[])
              : [parsed.status];
          }

          if (parsed.sort) {
            setSort(parsed.sort as string);

            body.sort = parsed.sort as string;
          }

          if (parsed.sortProp) {
            setSortProp(body.sortProp);
          }
        }

        getCustomerDocuments(body);
        getAllowedCategories();

        getCustomerOrganizationDocumentsStats(customerId);
      }
    }
  }, [customerId, activePermissions, customerType, search, selectedCategory]);

  useEffect(() => {
    if (
      selectedCategory &&
      documentsByCategoryId &&
      documentsByCategoryId[selectedCategory.id] &&
      ((cursorByCategoryId && !cursorByCategoryId[selectedCategory.id]) ||
        !cursorByCategoryId)
    ) {
      dispatch(
        setCursorByCategoryId({
          startCursor:
            documentsByCategoryId[selectedCategory.id][0].startCursor,
          endCursor: documentsByCategoryId[selectedCategory.id][0].endCursor,
          take: '20',
          categoryId: selectedCategory.id,
        }),
      );
    }
  }, [documentsByCategoryId, cursorByCategoryId, selectedCategory]);

  useEffect(() => {
    if (selectedDocumentToRename) {
      setDocumentName(selectedDocumentToRename.name);
    }
  }, [selectedDocumentToRename]);

  useEffect(() => {
    if (selectedDocumentsByGroupId) {
      let counter = 0;

      Object.keys(selectedDocumentsByGroupId)?.forEach((key) => {
        counter += selectedDocumentsByGroupId[key];
      });

      setSelectedDocumentsCountByCategory(counter);
    }
  }, [selectedDocumentsByGroupId]);

  return {
    selectedCategory,
    selectedDocumentsCountByCategory,
    setDeletePopupVisible,
    deleteInProgress,
    deletePopupVisible,
    onDelete,
    setRenamePopupVisible,
    setSelectedDocumentToRename,
    setDocumentName,
    setNameError,
    onRename,
    documentRenameInProgress,
    renamePopupVisible,
    documentName,
    selectedDocumentToRename,
    nameError,
    errors,
    expiredDocumentsVisible,
    setExpiredDocumentsVisible,
    search,
    setMenuVisible,
    menuVisible,
    moveInProgress,
    changeCustomerDocumentsCategoryAction,
    otherActionsMenuVisible,
    setOtherActionsMenuVisible,
    statusChangeInProgress,
    setSelectedStatus,
    changeCustomerDocumentsStatusAction,
    selectedStatus,
    getFiles,
    downloadInProgress,
    isFetching,
    searchText,
    activeLabel,
    onSearch,
    setActiveLabel,
    withLeftButton,
    onScrollButton,
    onCategoryClick,
    prevSearchText,
    prevStatuses,
    selectedStatuses,
    withScrollButton,
    onGroupSelect,
    onGroupByIdSelect,
    setSelectedStatuses,
    sort,
    onChangeSort,
    sortProp,
    isLoading,
    documentsIsFetching,
    onSetPage,
    deleteDocumentIsSuccess,
    setSubMenuVisible,
    getFile,
    subMenuVisible,
    onSelectStatuses,
    setSelectedSubMenuDocumentId,
    selectedSubMenuDocumentId,
  };
};

export default useDocuments;
